import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import {
  CONTACT_EMAIL,
  CONTACT_NAME,
  RESPONDENT_TYPE,
} from 'constants/formFields';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { MATERIAL_ASSESSMENT_RESPONDENT } from 'constants/materialAssessment';
import { getApiFieldErrorMessage } from 'utils/helpers';

import useAddUserModalForm from './hooks/useAddUserModalForm';

import type IAnswerTypeOption from
  'pages/MaterialAssessmentPages/MaterialAssessmentSurveyCreation/components/CreateSurvey/interfaces/IAnswerTypeOption';
import type IFieldError from 'store/types/IFieldError';
import type ICreateBoard from './interfaces/IAddUserModal';

import styles from './scss/AddUserModal.module.scss';

function AddUserModal({
  open, onClose, title, companyId,
  handleCreateRecipientUsers,
}: ICreateBoard) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const isLoading = false;

  const respondentOptions = Object.keys(MATERIAL_ASSESSMENT_RESPONDENT).map((key) => ({
    label: t(`materialityAssessment.respondentTypes.${key}`, key),
    value: key,
  }));

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
  } = useAddUserModalForm({ isPageLoading: isLoading });

  const onCancel = () => {
    onClose();
    reset();
  };

  const onCreateInvestorSubmit = handleSubmit(async (data) => {
    const { contactEmail, contactName, respondentType } = data;
    const trimmedContactEmail = contactEmail.trim();

    try {
      await handleCreateRecipientUsers({
        email: trimmedContactEmail,
        name: contactName || '',
        respondentType: respondentType?.value || '',
        companyId,
      });
      onCancel();
    } catch (error) {
      setError(CONTACT_EMAIL, {
        type: 'server',
        message: getApiFieldErrorMessage(
          error,
          t('notifications.errorCreatedRecipientUsers'),
        ),
      }, { shouldFocus: true });
    }
  });

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.formWrapper}>
          <Controller
            control={control}
            name={CONTACT_EMAIL}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.email')}
                error={error?.message}
                onChange={(event) => {
                  clearErrors(CONTACT_EMAIL);
                  onChange(event.target.value);
                }}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={CONTACT_NAME}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value || ''}
                label={t('materialityAssessment.fields.name')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
              />
            )}
          />
          <Controller
            control={control}
            name={RESPONDENT_TYPE}
            render={({
              field: {
                name,
                value: respondentType,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                name={name}
                value={respondentOptions.find((option) => option?.value === respondentType?.value)}
                label={t('materialityAssessment.respondentRole')}
                error={(error as IFieldError)?.value?.message}
                options={respondentOptions}
                onChange={onChange}
                menuPosition="absolute"
                modalRef={modalRef}
                required
                formatOptionLabel={(option: IAnswerTypeOption) => (
                  <div>
                    <span>
                      {t(`materialityAssessment.${option.label}`)}
                    </span>
                  </div>
                )}
              />
            )}
          />
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onCancel}
          />
          <Button
            title={t('common.add')}
            onClick={onCreateInvestorSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}
export default AddUserModal;
